import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class ApiClient {
  private client: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth interceptor
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.token = localStorage.getItem('auth_token');
  }

  async login(staffId: string, pin: string) {
    const response = await this.client.post('/auth/login', { staffId, pin });
    this.token = response.data.accessToken;
    localStorage.setItem('auth_token', this.token);
    return response.data;
  }

  async validatePin(pin: string) {
    return this.client.post('/auth/validate-pin', { pin });
  }

  // Sales endpoints
  async createSale(branchId: string, saleData: any) {
    return this.client.post('/api/sales', {
      branchId,
      ...saleData,
    });
  }

  async getSales(branchId: string, startDate?: string, endDate?: string) {
    return this.client.get('/api/sales', {
      params: { branchId, startDate, endDate, limit: 100 },
    });
  }

  async getSaleDetail(saleId: string) {
    return this.client.get(`/api/sales/${saleId}`);
  }

  async refundSale(saleId: string) {
    return this.client.post(`/api/sales/${saleId}/refund`);
  }

  // Inventory endpoints
  async getInventory(branchId: string) {
    return this.client.get('/api/inventory', { params: { branchId } });
  }

  async adjustInventory(productId: string, branchId: string, quantity: number, type: string, reason?: string) {
    return this.client.post('/api/inventory/adjust', {
      productId,
      branchId,
      quantity,
      type,
      reason,
    });
  }

  async transferInventory(fromBranchId: string, toBranchId: string, items: any[], reason?: string) {
    return this.client.post('/api/inventory/transfer', {
      fromBranchId,
      toBranchId,
      items,
      reason,
    });
  }

  // Shifts endpoints
  async openShift(branchId: string, floatOpening: string, floatOverrideReason?: string, mahlomolaPin?: string) {
    return this.client.post('/api/shifts/open', {
      branchId,
      floatOpening,
      floatOverrideReason,
      mahlomolaPin,
    });
  }

  async getCurrentShift(branchId: string) {
    return this.client.get('/api/shifts/current', { params: { branchId } });
  }

  async closeShift(shiftId: string, countedCash: string, notes?: string) {
    return this.client.post('/api/shifts/close', {
      shiftId,
      countedCash,
      notes,
    });
  }

  async getFloatHistory(branchId: string) {
    return this.client.get('/api/shifts/float-history', { params: { branchId } });
  }

  async getShiftHistory(branchId: string) {
    return this.client.get('/api/shifts/history', { params: { branchId } });
  }

  // Reports endpoints
  async getDailySalesReport(branchId: string, date: string) {
    return this.client.get('/api/reports/daily-sales', { params: { branchId, date } });
  }

  async getProductPopularity(branchId: string, days?: number) {
    return this.client.get('/api/reports/product-popularity', { params: { branchId, days } });
  }

  async getInventoryReport(branchId: string) {
    return this.client.get('/api/reports/inventory', { params: { branchId } });
  }

  async getRevenueTrend(branchId: string, days?: number) {
    return this.client.get('/api/reports/revenue-trend', { params: { branchId, days } });
  }

  // Compliance endpoints
  async createComplianceTask(branchId: string, data: any) {
    return this.client.post('/api/compliance/tasks', { branchId, ...data });
  }

  async getComplianceTasks(branchId: string, status?: string, staffId?: string) {
    return this.client.get('/api/compliance/tasks', { params: { branchId, status, staffId } });
  }

  async getComplianceTask(taskId: string) {
    return this.client.get(`/api/compliance/tasks/${taskId}`);
  }

  async updateComplianceTask(taskId: string, data: any) {
    return this.client.put(`/api/compliance/tasks/${taskId}`, data);
  }

  async completeComplianceTask(taskId: string, completedByStaffId: string, notes?: string) {
    return this.client.put(`/api/compliance/tasks/${taskId}/complete`, { completedByStaffId, notes });
  }

  async createTrainingRecord(data: any) {
    return this.client.post('/api/compliance/training', data);
  }

  async getStaffTrainingRecords(staffId: string) {
    return this.client.get(`/api/compliance/training/staff/${staffId}`);
  }

  async getExpiringCertificates(branchId: string, daysUntilExpiry: number = 30) {
    return this.client.get('/api/compliance/expiring-certificates', {
      params: { branchId, daysUntilExpiry },
    });
  }

  async getComplianceDashboard(branchId: string) {
    return this.client.get('/api/compliance/dashboard', { params: { branchId } });
  }

  async getComplianceReport(branchId: string, month: string) {
    return this.client.get('/api/compliance/report', { params: { branchId, month } });
  }

  // Production endpoints
  async createRecipe(productId: string, yield_: number, instructions?: string) {
    return this.client.post('/api/production/recipes', { productId, yield: yield_, instructions });
  }

  async getRecipe(productId: string) {
    return this.client.get('/api/production/recipes', { params: { productId } });
  }

  // Everyday bake logging is by product; recipeId is optional detail
  async logProduction(
    branchId: string,
    productId: string,
    quantity: number,
    notes?: string,
    shiftId?: string,
  ) {
    return this.client.post('/api/production/logs', {
      productId,
      branchId,
      quantity,
      shiftId,
      notes,
    });
  }

  async logProductionByRecipe(
    recipeId: string,
    branchId: string,
    quantity: number,
    shiftId?: string,
    notes?: string,
  ) {
    return this.client.post('/api/production/logs', { recipeId, branchId, quantity, shiftId, notes });
  }

  // Deliveries — checking what came against what was ordered
  async pendingDeliveries(supplierId?: string) {
    return this.client.get('/api/deliveries/pending', { params: { supplierId } });
  }

  async compareDelivery(orderId: string) {
    return this.client.get(`/api/deliveries/${orderId}`);
  }

  async reconcileDelivery(
    orderId: string,
    data: {
      invoiceNumber?: string;
      deliveryNoteNumber?: string;
      invoiceDate?: string;
      invoicedTotal?: string;
      note?: string;
      lines: Array<{ itemId: string; receivedQuantity: number; chargedPrice?: string }>;
    },
  ) {
    return this.client.post(`/api/deliveries/${orderId}/reconcile`, data);
  }

  // Ingredients — flour, sugar and the rest
  async listIngredients(branchId: string, includeInactive = false) {
    return this.client.get('/api/ingredients', { params: { branchId, includeInactive } });
  }

  async ingredientSummary(branchId: string) {
    return this.client.get('/api/ingredients/summary', { params: { branchId } });
  }

  async ingredientMovements(branchId: string, ingredientId?: string) {
    return this.client.get('/api/ingredients/movements', { params: { branchId, ingredientId } });
  }

  async createIngredient(name: string, unit: string, lowThreshold?: string, unitCost?: string) {
    return this.client.post('/api/ingredients', { name, unit, lowThreshold, unitCost });
  }

  async moveIngredient(
    branchId: string,
    ingredientId: string,
    reason: string,
    quantity: string,
    note?: string,
  ) {
    return this.client.post('/api/ingredients/move', { branchId, ingredientId, reason, quantity, note });
  }

  // Cleaning — the daily checklist that gates closing the till
  async cleaningToday(branchId: string) {
    return this.client.get('/api/cleaning', { params: { branchId } });
  }

  async setCleaningChecked(itemId: string, branchId: string, done: boolean, note?: string) {
    return this.client.put(`/api/cleaning/${itemId}`, { branchId, done, note });
  }

  async seedCleaningList(branchId: string) {
    return this.client.post('/api/cleaning/seed', { branchId });
  }

  async addCleaningItem(branchId: string, section: string, title: string) {
    return this.client.post('/api/cleaning/items', { branchId, section, title });
  }

  async setCleaningItemActive(itemId: string, active: boolean) {
    return this.client.put(`/api/cleaning/items/${itemId}/active`, { active });
  }

  // Crates — who is holding them
  async crateBalances(branchId: string, includeSettled = false) {
    return this.client.get('/api/crates', { params: { branchId, includeSettled } });
  }

  async crateSummary(branchId: string) {
    return this.client.get('/api/crates/summary', { params: { branchId } });
  }

  async crateHistory(branchId: string, customerName?: string) {
    return this.client.get('/api/crates/history', { params: { branchId, customerName } });
  }

  async recordCrateMovement(
    branchId: string,
    data: {
      customerName: string;
      customerPhone?: string;
      direction: 'out' | 'back';
      quantity: number;
      depositPerCrate?: string;
      note?: string;
    },
  ) {
    return this.client.post('/api/crates', { branchId, ...data });
  }

  // Prepayments — paid now, collected later
  async listPrepayments(branchId: string, status = 'held') {
    return this.client.get('/api/prepayments', { params: { branchId, status } });
  }

  async outstandingPrepayments(branchId: string) {
    return this.client.get('/api/prepayments/outstanding', { params: { branchId } });
  }

  async createPrepayment(
    branchId: string,
    data: {
      customerName: string;
      customerPhone?: string;
      amount: string;
      paymentMethod: string;
      forWhat?: string;
      shiftId?: string;
    },
  ) {
    return this.client.post('/api/prepayments', { branchId, ...data });
  }

  async resolvePrepayment(prepaymentId: string, outcome: 'fulfilled' | 'refunded', note?: string) {
    return this.client.put(`/api/prepayments/${prepaymentId}/resolve`, { outcome, note });
  }

  // Catalogue — the range you sell and the shops you sell it from
  async listProducts(includeInactive = false) {
    return this.client.get('/api/products', { params: { includeInactive } });
  }

  async createProduct(name: string, category: string, price: string, lowThreshold?: number, branchIds?: string[], zeroRated?: boolean) {
    return this.client.post('/api/products', { name, category, price, lowThreshold, branchIds, zeroRated });
  }

  async updateProduct(
    productId: string,
    patch: { name?: string; category?: string; price?: string; zeroRated?: boolean; lowThreshold?: number; active?: boolean },
  ) {
    return this.client.put(`/api/products/${productId}`, patch);
  }

  async stockProductAt(productId: string, branchId: string, quantity = 0) {
    return this.client.post(`/api/products/${productId}/stock-at`, { branchId, quantity });
  }

  async listShops(includeInactive = false) {
    return this.client.get('/api/shops', { params: { includeInactive } });
  }

  async createShop(name: string, location: string) {
    return this.client.post('/api/shops', { name, location });
  }

  async updateShop(branchId: string, patch: { name?: string; location?: string; active?: boolean }) {
    return this.client.put(`/api/shops/${branchId}`, patch);
  }

  // Notes — the staff message board
  async listNotes(branchId: string, includeResolved = false) {
    return this.client.get('/api/notes', { params: { branchId, includeResolved } });
  }

  async createNote(branchId: string, message: string) {
    return this.client.post('/api/notes', { branchId, message });
  }

  async resolveNote(noteId: string) {
    return this.client.put(`/api/notes/${noteId}/resolve`, {});
  }

  async reopenNote(noteId: string) {
    return this.client.put(`/api/notes/${noteId}/reopen`, {});
  }

  // Attendance — clocking in and out
  async clockIn(branchId?: string, location?: { latitude: number; longitude: number }) {
    return this.client.post('/api/attendance/check-in', { branchId, location });
  }

  async clockOut(branchId?: string) {
    return this.client.post('/api/attendance/check-out', { branchId });
  }

  async whoIsIn(branchId: string) {
    return this.client.get('/api/attendance/who-is-in', { params: { branchId } });
  }

  async getMyAttendanceStats(days = 30) {
    return this.client.get('/api/attendance/stats', { params: { days } });
  }

  async getAttendanceReport(branchId: string, startDate: string, endDate: string) {
    return this.client.get('/api/attendance/report', { params: { branchId, startDate, endDate } });
  }

  // Petty cash — money out of the drawer, feeds the cash-up expected figure
  async recordPettyCash(branchId: string, amount: number, reason: string, shiftId?: string) {
    return this.client.post('/api/petty-cash', { branchId, amount, reason, shiftId });
  }

  async listPettyCash(branchId: string, shiftId?: string) {
    return this.client.get('/api/petty-cash', { params: { branchId, shiftId } });
  }

  // Staff administration
  async listStaff(branchId?: string, includeInactive = false) {
    return this.client.get('/api/staff', { params: { branchId, includeInactive } });
  }

  async createStaff(name: string, pin: string, role: string, branchId: string) {
    return this.client.post('/api/staff', { name, pin, role, branchId });
  }

  async updateStaff(staffId: string, updates: { role?: string; branchId?: string; active?: boolean }) {
    return this.client.put(`/api/staff/${staffId}`, updates);
  }

  async changeOwnPin(currentPin: string, newPin: string) {
    return this.client.post('/api/staff/change-pin', { currentPin, newPin });
  }

  async resetStaffPin(staffId: string, newPin: string, managerPin: string) {
    return this.client.post('/api/staff/reset-pin', { staffId, newPin, managerPin });
  }

  async getBranches() {
    return this.client.get('/api/branches');
  }

  async getInventoryMovements(branchId: string, limit: number = 50) {
    return this.client.get('/api/inventory/movements', { params: { branchId, limit } });
  }

  async getProductionHistory(branchId: string, limit: number = 50) {
    return this.client.get('/api/production/history', { params: { branchId, limit } });
  }

  // Rota endpoints
  async createRotaPeriod(branchId: string, startDate: string, endDate: string, mode?: string) {
    return this.client.post('/api/rota/periods', { startDate, endDate, mode }, { params: { branchId } });
  }

  async listRotaPeriods(branchId: string, limit: number = 10, offset: number = 0) {
    return this.client.get('/api/rota/periods', { params: { branchId, limit, offset } });
  }

  async getRotaPeriod(periodId: string) {
    return this.client.get(`/api/rota/periods/${periodId}`);
  }

  async publishRotaPeriod(periodId: string) {
    return this.client.put(`/api/rota/periods/${periodId}/publish`, {});
  }

  async assignShift(rotaPeriodId: string, staffId: string, shiftDate: string, shiftPattern: string) {
    return this.client.post('/api/rota/shifts/assign', {
      rotaPeriodId,
      staffId,
      shiftDate,
      shiftPattern,
    });
  }

  async requestShift(rotaPeriodId: string, staffId: string, shiftDate: string, shiftPattern: string) {
    return this.client.post('/api/rota/shifts/request', {
      rotaPeriodId,
      staffId,
      shiftDate,
      shiftPattern,
    });
  }

  async approveShift(shiftId: string, approvedByStaffId: string) {
    return this.client.put(`/api/rota/shifts/${shiftId}/approve`, { approvedByStaffId });
  }

  async rejectShift(shiftId: string, approvedByStaffId: string, reason?: string) {
    return this.client.put(`/api/rota/shifts/${shiftId}/reject`, { approvedByStaffId, reason });
  }

  async getStaffRota(staffId: string, startDate?: string, endDate?: string) {
    return this.client.get(`/api/rota/staff/${staffId}`, { params: { startDate, endDate } });
  }

  async getAvailableShifts(periodId: string, staffId: string) {
    return this.client.get(`/api/rota/periods/${periodId}/available`, { params: { staffId } });
  }

  async getShiftPatterns() {
    return this.client.get('/api/rota/patterns');
  }

  async createShiftPattern(data: any) {
    return this.client.post('/api/rota/patterns', data);
  }

  async updateShiftPattern(patternId: string, data: any) {
    return this.client.put(`/api/rota/patterns/${patternId}`, data);
  }

  async getRotaAnalytics(branchId: string, periodId: string) {
    return this.client.get(`/api/rota/analytics/${periodId}`, { params: { branchId } });
  }

  // Alerts endpoints
  async getInventoryAlerts(branchId: string) {
    return this.client.get('/api/alerts/inventory', { params: { branchId } });
  }

  // Forecasting endpoints
  async getDailyForecast(branchId: string) {
    return this.client.get('/api/forecasting/daily', { params: { branchId } });
  }

  async getTrendAnalysis(branchId: string) {
    return this.client.get('/api/forecasting/trend', { params: { branchId } });
  }

  // Alert Rules endpoints
  async getAlertRules(branchId: string) {
    return this.client.get('/api/alert-rules', { params: { branchId } });
  }

  async createAlertRule(branchId: string, ruleData: any) {
    return this.client.post('/api/alert-rules', ruleData, { params: { branchId } });
  }

  async updateAlertRule(ruleId: string, updates: any) {
    return this.client.put(`/api/alert-rules/${ruleId}`, updates);
  }

  async deleteAlertRule(ruleId: string) {
    return this.client.delete(`/api/alert-rules/${ruleId}`);
  }

  // Audit Log endpoints
  async getAuditLog(branchId: string, limit: number = 100, offset: number = 0) {
    return this.client.get('/api/audit-log', { params: { branchId, limit, offset } });
  }

  async searchAuditLog(branchId: string, query: string, limit: number = 50) {
    return this.client.get('/api/audit-log/search', { params: { branchId, query, limit } });
  }

  // Export endpoints
  async exportSalesReport(branchId: string, startDate: string, endDate: string, format: 'csv' | 'json' = 'csv') {
    const response = await this.client.get('/api/export/sales-report', {
      params: { branchId, startDate, endDate, format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportInventoryReport(branchId: string, format: 'csv' | 'json' = 'csv') {
    const response = await this.client.get('/api/export/inventory-report', {
      params: { branchId, format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportStaffRota(branchId: string, startDate: string, endDate: string, format: 'csv' | 'json' = 'csv') {
    const response = await this.client.get('/api/export/staff-rota', {
      params: { branchId, startDate, endDate, format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportComplianceReport(branchId: string, month: string, format: 'csv' | 'json' = 'csv') {
    const response = await this.client.get('/api/export/compliance-report', {
      params: { branchId, month, format },
      responseType: 'blob',
    });
    return response.data;
  }

  async exportProductPerformance(branchId: string, days: number = 30, format: 'csv' | 'json' = 'csv') {
    const response = await this.client.get('/api/export/product-performance', {
      params: { branchId, days, format },
      responseType: 'blob',
    });
    return response.data;
  }

  // Analytics endpoints
  async getRevenueAnalytics(branchId: string, startDate: string, endDate: string, period: 'daily' | 'weekly' | 'monthly' = 'daily') {
    return this.client.get('/api/analytics/revenue', { params: { branchId, startDate, endDate, period } });
  }

  async getPaymentMethodAnalytics(branchId: string, startDate: string, endDate: string) {
    return this.client.get('/api/analytics/payment-methods', { params: { branchId, startDate, endDate } });
  }

  async getProductPerformanceAnalytics(branchId: string, startDate: string, endDate: string, limit: number = 20) {
    return this.client.get('/api/analytics/product-performance', { params: { branchId, startDate, endDate, limit } });
  }

  async getStaffPerformanceAnalytics(branchId: string, startDate: string, endDate: string) {
    return this.client.get('/api/analytics/staff-performance', { params: { branchId, startDate, endDate } });
  }

  async getInventoryTurnover(branchId: string, days: number = 30) {
    return this.client.get('/api/analytics/inventory-turnover', { params: { branchId, days } });
  }

  async getDiscountAnalytics(branchId: string, startDate: string, endDate: string) {
    return this.client.get('/api/analytics/discount-analysis', { params: { branchId, startDate, endDate } });
  }

  async getAccountAnalytics(branchId: string) {
    return this.client.get('/api/analytics/account-analysis', { params: { branchId } });
  }

  // Notifications endpoints
  async getNotifications(branchId: string, staffId?: string, unreadOnly: boolean = false) {
    return this.client.get('/api/notifications', { params: { branchId, staffId, unreadOnly } });
  }

  async markNotificationAsRead(notificationId: string) {
    return this.client.put(`/api/notifications/${notificationId}/read`, {});
  }

  async sendTestNotification(branchId: string, type: 'alert' | 'info' | 'warning' | 'error', title: string, message: string) {
    return this.client.post('/api/notifications/test', { branchId, type, title, message });
  }

  // Settings endpoints
  async getSettings() {
    return this.client.get('/api/settings');
  }

  async updateSettings(settings: any) {
    return this.client.put('/api/settings', settings);
  }

  async calculateTax(amount: number) {
    return this.client.get('/api/settings/tax-calculator', { params: { amount } });
  }

  async calculateBulkDiscount(itemCount: number) {
    return this.client.get('/api/settings/bulk-discount', { params: { itemCount } });
  }

  async checkFloatVariance(variance: number) {
    return this.client.get('/api/settings/float-check', { params: { variance } });
  }

  // Time-off endpoints
  async requestLeave(staffId: string, leaveType: string, startDate: string, endDate: string, reason: string) {
    return this.client.post('/api/time-off/request', {
      staffId,
      leaveType,
      startDate,
      endDate,
      reason,
    });
  }

  async approveLeave(leaveId: string, approvedBy: string) {
    return this.client.put(`/api/time-off/${leaveId}/approve`, { approvedBy });
  }

  async rejectLeave(leaveId: string, approvedBy: string, reason: string) {
    return this.client.put(`/api/time-off/${leaveId}/reject`, { approvedBy, reason });
  }

  async cancelLeave(leaveId: string) {
    return this.client.put(`/api/time-off/${leaveId}/cancel`, {});
  }

  async getLeaveRequests(branchId?: string, status?: string) {
    return this.client.get('/api/time-off/requests', { params: { branchId, status } });
  }

  async getStaffLeaveRequests(staffId: string) {
    return this.client.get(`/api/time-off/staff/${staffId}/requests`);
  }

  async getLeaveBalance(staffId: string) {
    return this.client.get(`/api/time-off/staff/${staffId}/balance`);
  }

  async getLeaveReport(branchId: string, month: string) {
    return this.client.get('/api/time-off/report', { params: { branchId, month } });
  }

  // Rewards endpoints
  async earnPointsFromSale(accountId: string, saleTotal: number, saleId: string) {
    return this.client.post('/api/rewards/earn', {
      accountId,
      saleTotal,
      saleId,
    });
  }

  async redeemPoints(accountId: string, points: number, reason: string) {
    return this.client.post('/api/rewards/redeem', {
      accountId,
      points,
      reason,
    });
  }

  async getRewardAccount(accountId: string) {
    return this.client.get(`/api/rewards/account/${accountId}`);
  }

  async getRewardsHistory(accountId: string, limit?: number) {
    return this.client.get(`/api/rewards/history/${accountId}`, { params: { limit } });
  }

  async getRedemptionCatalog() {
    return this.client.get('/api/rewards/catalog');
  }

  async createRedemptionOption(name: string, pointsCost: number, category: string, description?: string) {
    return this.client.post('/api/rewards/catalog', {
      name,
      pointsCost,
      category,
      description,
    });
  }

  async getTierBenefits(tierLevel: string) {
    return this.client.get(`/api/rewards/tier/${tierLevel}`);
  }

  async getRewardsReport(month: string) {
    return this.client.get('/api/rewards/report', { params: { month } });
  }

  // Accounts endpoints
  async createAccount(
    name: string,
    contact: string,
    creditLimit: number,
    accountType: string,
    email?: string,
    phone?: string,
    address?: string,
    taxId?: string
  ) {
    return this.client.post('/api/accounts', {
      name,
      contact,
      creditLimit,
      accountType,
      email,
      phone,
      address,
      taxId,
    });
  }

  async getAccount(accountId: string) {
    return this.client.get(`/api/accounts/${accountId}`);
  }

  async searchAccounts(search: string) {
    return this.client.get('/api/accounts', { params: { search } });
  }

  async updateAccount(accountId: string, updates: any) {
    return this.client.put(`/api/accounts/${accountId}`, updates);
  }

  async chargeAccount(accountId: string, amount: number, description: string, saleId?: string) {
    return this.client.post(`/api/accounts/${accountId}/charge`, {
      amount,
      description,
      saleId,
    });
  }

  async makePayment(accountId: string, amount: number, paymentMethod: string) {
    return this.client.post(`/api/accounts/${accountId}/payment`, {
      amount,
      paymentMethod,
    });
  }

  async issueCredit(accountId: string, amount: number, reason: string) {
    return this.client.post(`/api/accounts/${accountId}/credit`, {
      amount,
      reason,
    });
  }

  async getAccountTransactions(accountId: string, limit?: number, offset?: number) {
    return this.client.get(`/api/accounts/${accountId}/transactions`, {
      params: { limit, offset },
    });
  }

  async getAccountStatement(accountId: string, month: string) {
    return this.client.get(`/api/accounts/${accountId}/statement`, { params: { month } });
  }

  async getAccountsReport() {
    return this.client.get('/api/accounts/report');
  }

  // Suppliers endpoints
  async createSupplier(
    name: string,
    contact: string,
    email?: string,
    phone?: string,
    address?: string,
    paymentTerms?: string
  ) {
    return this.client.post('/api/suppliers', {
      name,
      contact,
      email,
      phone,
      address,
      paymentTerms,
    });
  }

  async getSupplier(supplierId: string) {
    return this.client.get(`/api/suppliers/${supplierId}`);
  }

  async listSuppliers(status?: string) {
    return this.client.get('/api/suppliers', { params: { status } });
  }

  async updateSupplier(supplierId: string, updates: any) {
    return this.client.put(`/api/suppliers/${supplierId}`, updates);
  }

  // Purchase Orders endpoints
  async createPurchaseOrder(
    supplierId: string,
    items: any[],
    expectedDelivery: string,
    notes?: string
  ) {
    return this.client.post('/api/suppliers/orders', {
      supplierId,
      items,
      expectedDelivery,
      notes,
    });
  }

  async getPurchaseOrder(poId: string) {
    return this.client.get(`/api/suppliers/orders/${poId}`);
  }

  async listPurchaseOrders(supplierId?: string, status?: string) {
    return this.client.get('/api/suppliers/orders', { params: { supplierId, status } });
  }

  async confirmPurchaseOrder(poId: string) {
    return this.client.put(`/api/suppliers/orders/${poId}/confirm`, {});
  }

  async receivePurchaseOrder(
    poId: string,
    items: any[],
    receivedBy: string,
    notes?: string
  ) {
    return this.client.post(`/api/suppliers/orders/${poId}/receive`, {
      items,
      receivedBy,
      notes,
    });
  }

  async cancelPurchaseOrder(poId: string, reason: string) {
    return this.client.put(`/api/suppliers/orders/${poId}/cancel`, { reason });
  }

  async getSupplierPerformance() {
    return this.client.get('/api/suppliers/performance');
  }

  // Audit & Compliance Reporting endpoints
  async generateAuditReport(
    startDate: string,
    endDate: string,
    branchId?: string
  ) {
    return this.client.post('/api/audit/report', {}, {
      params: { startDate, endDate, branchId },
    });
  }

  async getAuditTrail(
    startDate: string,
    endDate: string,
    eventType?: string,
    staffId?: string
  ) {
    return this.client.get('/api/audit/trail', {
      params: { startDate, endDate, eventType, staffId },
    });
  }

  async getStaffAuditTrail(staffId: string, days?: number) {
    return this.client.get(`/api/audit/staff/${staffId}/trail`, {
      params: { days },
    });
  }

  async getComplianceCalendar(year: number, month: number) {
    return this.client.get('/api/audit/compliance-calendar', {
      params: { year, month },
    });
  }

  async generateComplianceReport(month: string) {
    return this.client.post('/api/audit/compliance-report', {}, {
      params: { month },
    });
  }

  async getDataIntegrityReport(startDate: string, endDate: string) {
    return this.client.post('/api/audit/data-integrity', {}, {
      params: { startDate, endDate },
    });
  }

  async exportAuditLog(
    startDate: string,
    endDate: string,
    format: 'csv' | 'json' = 'csv'
  ) {
    return this.client.post('/api/audit/export', {}, {
      params: { startDate, endDate, format },
      responseType: 'blob',
    });
  }

  private downloadFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  logout() {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  isOnline(): boolean {
    return navigator.onLine;
  }
}

export const apiClient = new ApiClient();
